/**
 * Payment Methods Tab
 * 
 * Allows unit administrators to configure payment methods:
 * - Bank Transfer (account details)
 * - Stripe (connected account status)
 * - SumUp (API key management)
 * - SlickPay (Open Banking bank details)
 */

import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Building2, CreditCard, Smartphone, CheckCircle, XCircle, AlertCircle, Wallet, Clock } from 'lucide-react';

interface PaymentMethodsTabProps {
  unit: any;
  db: any;
  functions: any;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  Button: any;
  Input: any;
  Label: any;
  Card: any;
  CardContent: any;
  CardDescription: any;
  CardHeader: any;
  CardTitle: any;
  Switch: any;
  Badge: any;
  Select?: any;
  SelectTrigger?: any;
  SelectContent?: any;
  SelectItem?: any;
  SelectValue?: any;
  onUpdate: () => Promise<void>;
}

interface SystemPaymentSettings {
  bankTransfer?: { enabled: boolean; transactionFee: number };
  stripe?: { enabled: boolean; transactionFee: number };
  sumup?: { enabled: boolean; transactionFee: number };
  slickpay?: { enabled: boolean; transactionFee: number };
}

/** Format a Firestore Timestamp (client SDK) as e.g. "16 Sept 2026"; null when absent. */
function formatRegistrationDate(value: any): string | null {
  const date = typeof value?.toDate === 'function' ? value.toDate() : null;
  return date ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
}

export function PaymentMethodsTab({
  unit,
  db,
  functions,
  showSuccess,
  showError,
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  onUpdate,
}: PaymentMethodsTabProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingApiKey, setTestingApiKey] = useState(false);
  
  // System-level payment settings
  const [systemPaymentSettings, setSystemPaymentSettings] = useState<SystemPaymentSettings>({});
  
  // Bank Transfer
  const [bankTransferEnabled, setBankTransferEnabled] = useState(false);
  const [nameOnAccount, setNameOnAccount] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  
  // Stripe
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  
  // SumUp
  const [sumupEnabled, setSumupEnabled] = useState(false);
  const [sumupApiKey, setSumupApiKey] = useState('');
  const [sumupApiKeyLastFour, setSumupApiKeyLastFour] = useState('');
  const [sumupConfigured, setSumupConfigured] = useState(false);
  const [sumupEnvironment, setSumupEnvironment] = useState<'production' | 'sandbox'>('production');

  // SlickPay (Open Banking creditor account — separate from manual bank transfer)
  const [slickpayEnabled, setSlickpayEnabled] = useState(false);
  const [slickpayNameOnAccount, setSlickpayNameOnAccount] = useState('');
  const [slickpaySortCode, setSlickpaySortCode] = useState('');
  const [slickpayAccountNumber, setSlickpayAccountNumber] = useState('');

  const hasSlickpayBankDetails = (
    name: string,
    sort: string,
    account: string
  ) => !!(name.trim() && sort.trim() && account.trim());

  // Hope Macy verification state, written only by Cloud Functions (slickpay/unitRegistration.ts).
  const slickpayRegistration = unit.settings?.paymentMethods?.slickpay?.registration;
  const slickpayStatus: 'active' | 'pending' | 'none' =
    slickpayRegistration?.status === 'active' || slickpayRegistration?.status === 'pending'
      ? slickpayRegistration.status
      : 'none';
  const slickpaySubmittedOn = formatRegistrationDate(slickpayRegistration?.requestedAt);

  // Fetch system payment settings and unit payment methods
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch system settings
        const systemSettingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (systemSettingsDoc.exists()) {
          const data = systemSettingsDoc.data();
          setSystemPaymentSettings(data.paymentMethods || {});
        }
        
        // Load unit payment methods
        const paymentMethods = unit.settings?.paymentMethods || {};
        
        // Bank Transfer
        setBankTransferEnabled(paymentMethods.bankTransfer?.enabled || false);
        setNameOnAccount(paymentMethods.bankTransfer?.nameOnAccount || '');
        setSortCode(paymentMethods.bankTransfer?.sortCode || '');
        setAccountNumber(paymentMethods.bankTransfer?.accountNumber || '');
        
        // Stripe
        setStripeEnabled(paymentMethods.stripe?.enabled || false);
        setStripeConfigured(!!unit.stripeConnectedAccountId);
        
        // SumUp
        setSumupEnabled(paymentMethods.sumup?.enabled || false);
        setSumupApiKeyLastFour(paymentMethods.sumup?.apiKeyLastFour || '');
        setSumupConfigured(!!paymentMethods.sumup?.apiKeyEncrypted);
        setSumupEnvironment(paymentMethods.sumup?.environment || 'production');

        // SlickPay — own bank details; units set up before these were separate used the bank transfer ones
        setSlickpayEnabled(paymentMethods.slickpay?.enabled || false);
        const slick = paymentMethods.slickpay;
        const slickDetails = hasSlickpayBankDetails(
          slick?.nameOnAccount ?? '',
          slick?.sortCode ?? '',
          slick?.accountNumber ?? ''
        )
          ? slick
          : paymentMethods.bankTransfer;
        setSlickpayNameOnAccount(slickDetails?.nameOnAccount ?? '');
        setSlickpaySortCode(slickDetails?.sortCode ?? '');
        setSlickpayAccountNumber(slickDetails?.accountNumber ?? '');
        
      } catch (error) {
        console.error('Error fetching payment settings:', error);
        showError('Failed to load payment settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [unit, db]);

  const handleSaveBankTransfer = async () => {
    setSaving(true);
    try {
      // The enabled flag can be toggled directly, but the account details themselves
      // require an emailed confirmation before they take effect (they control where
      // member payments are sent).
      const detailsChanged =
        nameOnAccount.trim() !== (unit.settings?.paymentMethods?.bankTransfer?.nameOnAccount || '') ||
        sortCode.trim() !== (unit.settings?.paymentMethods?.bankTransfer?.sortCode || '') ||
        accountNumber.trim() !== (unit.settings?.paymentMethods?.bankTransfer?.accountNumber || '');

      if (bankTransferEnabled !== (unit.settings?.paymentMethods?.bankTransfer?.enabled || false)) {
        const unitRef = doc(db, 'units', unit.id);
        await updateDoc(unitRef, { 'settings.paymentMethods.bankTransfer.enabled': bankTransferEnabled });
        await onUpdate();
      }

      if (detailsChanged) {
        const { httpsCallable } = await import('firebase/functions');
        const requestUnitBankDetailsChange = httpsCallable(functions, 'requestUnitBankDetailsChange');
        await requestUnitBankDetailsChange({
          unitId: unit.id,
          paymentMethod: 'bankTransfer',
          nameOnAccount: nameOnAccount.trim(),
          sortCode: sortCode.trim(),
          accountNumber: accountNumber.trim(),
        });
        showSuccess('Check your email to confirm this bank details change — nothing has been updated yet.');
      } else {
        showSuccess('Bank transfer settings saved successfully');
      }
    } catch (error: any) {
      console.error('Error saving bank transfer settings:', error);
      showError(error?.message || 'Failed to save bank transfer settings');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectStripe = async () => {
    setStripeConnecting(true);
    let navigatingToStripe = false;
    try {
      const { httpsCallable } = await import('firebase/functions');
      const createStripeConnectedAccount = httpsCallable(functions, 'createStripeConnectedAccount');
      const result = await createStripeConnectedAccount({ unitId: unit.id });
      const data = result.data as { onboardingUrl?: string; existing?: boolean; accountId?: string };
      if (data.onboardingUrl) {
        // Keep loading state until the browser leaves this page (do not clear in finally).
        navigatingToStripe = true;
        window.location.assign(data.onboardingUrl);
        return;
      }
      if (data.existing && data.accountId) {
        await onUpdate();
        setStripeConfigured(true);
        showSuccess('Stripe account already connected.');
      } else {
        showError('No onboarding URL returned. Please try again.');
      }
    } catch (error: any) {
      console.error('Error connecting Stripe:', error);
      showError(error.message || 'Failed to connect Stripe account');
    } finally {
      if (!navigatingToStripe) {
        setStripeConnecting(false);
      }
    }
  };

  const handleSaveStripe = async () => {
    setSaving(true);
    try {
      const unitRef = doc(db, 'units', unit.id);
      await updateDoc(unitRef, { 'settings.paymentMethods.stripe.enabled': stripeEnabled });
      
      await onUpdate();
      showSuccess('Stripe settings saved successfully');
    } catch (error) {
      console.error('Error saving Stripe settings:', error);
      showError('Failed to save Stripe settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAndSaveSumUpApiKey = async () => {
    if (!sumupApiKey || sumupApiKey.trim().length === 0) {
      showError('Please enter a SumUp API key');
      return;
    }

    setTestingApiKey(true);
    try {
      // Call backend function to validate and encrypt API key
      const { httpsCallable } = await import('firebase/functions');
      const validateSumUpApiKey = httpsCallable(functions, 'validateSumUpApiKey');
      
      const result = await validateSumUpApiKey({
        unitId: unit.id,
        apiKey: sumupApiKey.trim(),
        environment: sumupEnvironment,
      });
      
      const data = result.data as any;
      
      if (data.valid) {
        // Update local state
        setSumupApiKeyLastFour(data.apiKeyLastFour);
        setSumupConfigured(true);
        setSumupApiKey(''); // Clear the input field
        
        await onUpdate();
        showSuccess('SumUp API key validated and saved successfully!');
      } else {
        showError(data.error || 'Invalid SumUp API key');
      }
    } catch (error: any) {
      console.error('Error validating SumUp API key:', error);
      showError(error.message || 'Failed to validate SumUp API key');
    } finally {
      setTestingApiKey(false);
    }
  };

  const handleToggleSumUp = async (enabled: boolean) => {
    setSumupEnabled(enabled);
    
    // If disabling, save immediately
    if (!enabled) {
      setSaving(true);
      try {
        const unitRef = doc(db, 'units', unit.id);
        await updateDoc(unitRef, { 'settings.paymentMethods.sumup.enabled': false });
        
        await onUpdate();
        showSuccess('SumUp disabled');
      } catch (error) {
        console.error('Error disabling SumUp:', error);
        showError('Failed to disable SumUp');
        setSumupEnabled(true); // Revert
      } finally {
        setSaving(false);
      }
    } else {
      // If enabling, just update local state (will save when API key is validated)
      if (!sumupConfigured) {
        showError('Please configure SumUp API key before enabling');
        setSumupEnabled(false);
      } else {
        // Already configured, just save the enabled state
        setSaving(true);
        try {
          const unitRef = doc(db, 'units', unit.id);
          await updateDoc(unitRef, { 'settings.paymentMethods.sumup.enabled': true });
          
          await onUpdate();
          showSuccess('SumUp enabled');
        } catch (error) {
          console.error('Error enabling SumUp:', error);
          showError('Failed to enable SumUp');
          setSumupEnabled(false); // Revert
        } finally {
          setSaving(false);
        }
      }
    }
  };

  const handleEnableSlickPay = async () => {
    if (!hasSlickpayBankDetails(slickpayNameOnAccount, slickpaySortCode, slickpayAccountNumber)) {
      showError('Please enter name on account, sort code and account number for Open Banking.');
      return;
    }

    setSaving(true);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const requestSlickPayActivation = httpsCallable(functions, 'requestSlickPayActivation');
      const result = await requestSlickPayActivation({
        unitId: unit.id,
        nameOnAccount: slickpayNameOnAccount.trim(),
        sortCode: slickpaySortCode.trim(),
        accountNumber: slickpayAccountNumber.trim(),
      });
      await onUpdate();
      if ((result.data as { status?: string } | undefined)?.status === 'active') {
        showSuccess('Hope Macy has already verified this account, so Open Banking is now switched on.');
      } else {
        showSuccess('Submitted for verification. Open Banking will switch on automatically once Hope Macy verifies this account.');
      }
    } catch (error: any) {
      console.error('Error enabling SlickPay:', error);
      showError(error?.message || 'Failed to submit Open Banking bank details');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckSlickPayStatus = async () => {
    setSaving(true);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const checkSlickPayRegistration = httpsCallable(functions, 'checkSlickPayRegistration');
      const result = await checkSlickPayRegistration({ unitId: unit.id });
      await onUpdate();
      if ((result.data as { status?: string } | undefined)?.status === 'active') {
        showSuccess('Hope Macy has verified this account. Open Banking is now switched on.');
      } else {
        showSuccess('Still waiting for Hope Macy to verify this account.');
      }
    } catch (error: any) {
      console.error('Error checking SlickPay verification:', error);
      showError(error?.message || 'Could not check verification status');
    } finally {
      setSaving(false);
    }
  };

  // Only offered once Hope Macy has verified the account; firestore.rules enforces the same.
  const handleToggleSlickPay = async (enabled: boolean) => {
    setSlickpayEnabled(enabled);
    setSaving(true);
    try {
      await updateDoc(doc(db, 'units', unit.id), { 'settings.paymentMethods.slickpay.enabled': enabled });
      await onUpdate();
      showSuccess(enabled ? 'Open Banking payments switched on' : 'Open Banking payments switched off');
    } catch (error) {
      console.error('Error updating SlickPay:', error);
      showError('Failed to update Open Banking');
      setSlickpayEnabled(!enabled);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
          <p className="text-muted-foreground">Loading payment settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Payment Methods</h3>
        <p className="text-sm text-muted-foreground">
          Configure how guests can pay for event tickets. Only methods enabled by platform administrators are available.
        </p>
      </div>

      {/* Bank Transfer */}
      {systemPaymentSettings.bankTransfer?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Building2 className="mr-2 h-5 w-5" />
                Bank Transfer
              </div>
              <Switch
                checked={bankTransferEnabled}
                onCheckedChange={setBankTransferEnabled}
              />
            </CardTitle>
            <CardDescription>
              Guests can pay via bank transfer. Payments require manual verification by your treasurer.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {bankTransferEnabled ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="nameOnAccount">Name on Account</Label>
                  <Input
                    id="nameOnAccount"
                    placeholder="e.g., Freemason Lodge No. 123"
                    value={nameOnAccount}
                    onChange={(e: any) => setNameOnAccount(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sortCode">Sort Code</Label>
                    <Input
                      id="sortCode"
                      placeholder="12-34-56"
                      value={sortCode}
                      onChange={(e: any) => setSortCode(e.target.value)}
                      maxLength={8}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      placeholder="12345678"
                      value={accountNumber}
                      onChange={(e: any) => setAccountNumber(e.target.value)}
                      maxLength={8}
                    />
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Enable bank transfer to configure your bank account details.
              </p>
            )}

            {bankTransferEnabled !== (unit.settings?.paymentMethods?.bankTransfer?.enabled || false) && (
              <p className="text-sm text-amber-700">
                {bankTransferEnabled ? 'Bank transfer will be enabled' : 'Bank transfer will be disabled'} for guests once you save.
              </p>
            )}

            <Button onClick={handleSaveBankTransfer} disabled={saving}>
              {saving ? 'Saving...' : 'Save Bank Details'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stripe */}
      {systemPaymentSettings.stripe?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <CreditCard className="mr-2 h-5 w-5" />
                Stripe
              </div>
              <Switch
                checked={stripeEnabled}
                onCheckedChange={setStripeEnabled}
                disabled={!stripeConfigured}
              />
            </CardTitle>
            <CardDescription>
              Accept card payments via Stripe. Requires a Stripe Connected Account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center space-x-2">
                {stripeConfigured ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium">Stripe Connected Account Configured</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium">Stripe Not Configured</span>
                  </>
                )}
              </div>
              {stripeConfigured && (
                <Badge variant="default">Active</Badge>
              )}
            </div>
            
            {!stripeConfigured && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Stripe Account Required</p>
                    <p>
                      Connect your unit to Stripe to accept card payments. You will complete Stripe&apos;s secure onboarding and then return here to enable Stripe payments.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleConnectStripe}
                  disabled={stripeConnecting}
                >
                  {stripeConnecting ? 'Connecting...' : 'Connect with Stripe'}
                </Button>
              </div>
            )}
            
            {stripeConfigured && (
              <Button onClick={handleSaveStripe} disabled={saving}>
                {saving ? 'Saving...' : 'Save Stripe Settings'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* SumUp */}
      {systemPaymentSettings.sumup?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Smartphone className="mr-2 h-5 w-5" />
                SumUp
              </div>
              <Switch
                checked={sumupEnabled}
                onCheckedChange={handleToggleSumUp}
              />
            </CardTitle>
            <CardDescription>
              Accept card payments via SumUp. Requires a SumUp API key.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sumupConfigured && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">API Key: ****{sumupApiKeyLastFour}</span>
                    <span className="text-xs text-muted-foreground">
                      Environment: {sumupEnvironment === 'production' ? 'Production' : 'Sandbox'}
                    </span>
                  </div>
                </div>
                <Badge variant="default">Configured</Badge>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="sumupEnvironment">Environment</Label>
              <Select
                value={sumupEnvironment}
                onValueChange={(value: 'production' | 'sandbox') => setSumupEnvironment(value)}
              >
                <SelectTrigger id="sumupEnvironment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="sandbox">Sandbox</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select whether this is a production or sandbox/test API key.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sumupApiKey">
                {sumupConfigured ? 'Update SumUp API Key' : 'SumUp API Key'}
              </Label>
              <Input
                id="sumupApiKey"
                type="password"
                placeholder="Enter your SumUp API key"
                value={sumupApiKey}
                onChange={(e: any) => setSumupApiKey(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Get your API key from the{' '}
                <a
                  href="https://developer.sumup.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  SumUp Developer Dashboard
                </a>
              </p>
            </div>
            
            <Button 
              onClick={handleTestAndSaveSumUpApiKey} 
              disabled={testingApiKey || !sumupApiKey}
            >
              {testingApiKey ? 'Testing...' : sumupConfigured ? 'Update & Test API Key' : 'Test & Save API Key'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* SlickPay */}
      {systemPaymentSettings.slickpay?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Wallet className="mr-2 h-5 w-5" />
                Open Banking (SlickPay)
              </div>
              {slickpayStatus === 'active' && (
                <Switch checked={slickpayEnabled} onCheckedChange={handleToggleSlickPay} disabled={saving} />
              )}
              {slickpayStatus === 'pending' && <Badge variant="secondary">Pending verification</Badge>}
            </CardTitle>
            <CardDescription>
              Guests pay via Open Banking on a secure hosted page. Funds go directly into your unit bank account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {slickpayStatus === 'active' && (
              <div className="flex items-start space-x-2 p-3 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                <span className="text-sm text-green-800">
                  {`Verified by Hope Macy. ${
                    slickpayEnabled
                      ? 'Guests can pay by Open Banking.'
                      : 'Open Banking payments are switched off; use the switch above to turn them back on.'
                  } These bank details are locked, so contact support if they need to change.`}
                </span>
              </div>
            )}
            {slickpayStatus === 'pending' && (
              <div className="flex items-start space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Clock className="h-5 w-5 text-amber-600 shrink-0" />
                <span className="text-sm text-amber-800">
                  {`Waiting for Hope Macy to verify this bank account${
                    slickpaySubmittedOn ? ` (submitted ${slickpaySubmittedOn})` : ''
                  }. Open Banking switches on automatically once it is verified.`}
                </span>
              </div>
            )}
            {slickpayStatus === 'none' && (
              <div className="flex items-start space-x-2 p-3 bg-muted rounded-lg">
                <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground">
                  Enter the bank account that should receive Open Banking payments, then click Enable SlickPay.
                  Hope Macy verifies the account before guests can pay, and the details can&apos;t be changed after
                  that. These details are separate from your bank transfer settings.
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="slickpayNameOnAccount">Name on Account</Label>
              <Input
                id="slickpayNameOnAccount"
                placeholder="e.g., Freemason Lodge No. 123"
                value={slickpayNameOnAccount}
                onChange={(e: any) => setSlickpayNameOnAccount(e.target.value)}
                disabled={slickpayStatus === 'active'}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slickpaySortCode">Sort Code</Label>
                <Input
                  id="slickpaySortCode"
                  placeholder="12-34-56"
                  value={slickpaySortCode}
                  onChange={(e: any) => setSlickpaySortCode(e.target.value)}
                  maxLength={8}
                  disabled={slickpayStatus === 'active'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slickpayAccountNumber">Account Number</Label>
                <Input
                  id="slickpayAccountNumber"
                  placeholder="12345678"
                  value={slickpayAccountNumber}
                  onChange={(e: any) => setSlickpayAccountNumber(e.target.value)}
                  maxLength={8}
                  disabled={slickpayStatus === 'active'}
                />
              </div>
            </div>

            {slickpayStatus !== 'active' && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleEnableSlickPay} disabled={saving}>
                  {saving
                    ? 'Saving...'
                    : slickpayStatus === 'pending'
                      ? 'Update and resubmit details'
                      : 'Enable SlickPay'}
                </Button>
                {slickpayStatus === 'pending' && (
                  <Button variant="outline" onClick={handleCheckSlickPayStatus} disabled={saving}>
                    Check verification status
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* No payment methods available */}
      {!systemPaymentSettings.bankTransfer?.enabled &&
       !systemPaymentSettings.stripe?.enabled &&
       !systemPaymentSettings.sumup?.enabled &&
       !systemPaymentSettings.slickpay?.enabled && (
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              No payment methods are currently enabled by platform administrators.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Contact support to enable payment processing.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}




